import { convertDDMMYYYtoYYYYMMDD } from '../../utils/dates';
import {
  CreateNewForecastPayload,
  ForecastWindowSpec,
  NewForecastDataSource
} from '../dashboard/types';
import { getBudgetForecastKind, getScenarioForecastKind } from '../dashboard/utils';
import { getFixedForecastWindowPayload } from './get-fixed-forecast-window-payload';
import { getRollingForecastWindowPayload } from './get-forecast-window-spec';
import { ForecastKindOption, ForecastTypeOption } from './types';

interface ScenarioWindowPayload {
  forecast_from_date: string;
  window_from?: string;
  window_spec: ForecastWindowSpec;
}

interface GetScenarioWindowPayloadParams {
  durationFixed: string;
  durationRolling: string;
  previousActuals: boolean;
  type?: ForecastTypeOption;
  windowFrom: string;
}

export interface GetCreateForecastRequestDataParams {
  datasource: NewForecastDataSource;
  durationFixed: string;
  durationRolling: string;
  from: string;
  kind: ForecastKindOption;
  name: string;
  previousActuals: boolean;
  sourceForecast: string;
  type?: ForecastTypeOption;
}

/**
 * Builds the API window fields for a 3-way forecast.
 * Rolling forecasts are aligned to full financial years, start forecasting from today,
 * and omit `window_from` because their boundaries are derived by the API.
 * Fixed forecasts retain the selected start date as `window_from` and use today as
 * `forecast_from_date` only when today falls inside the selected window, inclusively.
 *
 * @param params - Values selected in the 3-way forecast form.
 * @param params.durationFixed - Number of years selected for a fixed forecast.
 * @param params.durationRolling - Financial-year duration selected for a rolling forecast, such as `FY+2`.
 * @param params.previousActuals - Whether the rolling window should include two previous financial years.
 * @param params.type - Selected forecast type, used to distinguish fixed and rolling forecasts.
 * @param params.windowFrom - Selected Start date formatted as `yyyy-MM-dd`.
 * @returns The calculated forecast start date and API window fields.
 */
export const getScenarioWindowPayload = ({
  durationFixed,
  durationRolling,
  previousActuals,
  type,
  windowFrom
}: GetScenarioWindowPayloadParams): ScenarioWindowPayload => {
  if (type === ForecastTypeOption.Rolling) {
    return getRollingForecastWindowPayload({ duration: durationRolling, previousActuals });
  }

  return getFixedForecastWindowPayload({ duration: durationFixed, windowFrom });
};

/**
 * Builds the shared organisation forecast create request used by create and duplicate flows.
 *
 * @param params - Current organisation forecast form values.
 * @param params.datasource - Selected source used to derive the API forecast kind.
 * @param params.durationFixed - Selected fixed duration in years.
 * @param params.durationRolling - Selected rolling duration in financial years.
 * @param params.from - Selected Start date formatted as `dd/MM/yyyy`.
 * @param params.kind - Budget or 3-way forecast kind.
 * @param params.name - Forecast name entered by the user.
 * @param params.previousActuals - Whether a rolling forecast includes previous actuals.
 * @param params.sourceForecast - Source forecast ID when creating from an existing forecast.
 * @param params.type - Selected fixed or rolling forecast type.
 * @returns The organisation forecast create payload.
 */
export const getCreateForecastRequestData = ({
  datasource,
  durationFixed,
  durationRolling,
  from,
  kind,
  name,
  previousActuals,
  sourceForecast,
  type
}: GetCreateForecastRequestDataParams): CreateNewForecastPayload => {
  const forecastFromDate = convertDDMMYYYtoYYYYMMDD(from);
  const scenarioWindowPayload =
    kind === 'scenario'
      ? getScenarioWindowPayload({
        durationFixed,
        durationRolling,
        previousActuals,
        type,
        windowFrom: forecastFromDate!
      })
      : {};

  return {
    name,
    kind:
      kind === 'budget'
        ? getBudgetForecastKind(datasource)
        : getScenarioForecastKind(datasource),
    categories_with_ai_enabled:
      datasource === NewForecastDataSource.WithAutoPredictions
        ? ['income', 'cost_of_sales', 'expenses', 'other_income', 'other_expenses']
        : null,
    forecast_from_date: forecastFromDate,
    include_unpaid_invoices: kind === 'scenario' && type === ForecastTypeOption.Rolling,
    from_forecast_id: datasource === NewForecastDataSource.FromDuplicate ? sourceForecast : undefined,
    ...(kind === 'budget'
      ? {
        window_from: forecastFromDate,
        window_spec: {
          kind: 'custom-years' as const,
          duration_years: 1 as const
        }
      }
      : {}),
    ...scenarioWindowPayload
  };
};
